import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { SignUpSchema, SignUpInput } from "../../lib/schemas";
import { useAuth } from "../../contexts/AuthContext";
import { CURRENT_TERMS_VERSION } from "../../lib/terms-version";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Alert, AlertDescription } from "../ui/alert";
import { Loader2, Mail, Lock, User, Eye, EyeOff } from "lucide-react";
import { getBrandName } from "../../services/supportProduct";
import { logError, ErrorFactory } from "../../lib/error-handling";
import { LazyIcon } from "../../lib/lazy-icons";
import i18n from "@core/i18n";

function translateAuthError(message: string): string {
  const m = message.toLowerCase();
  if (
    m.includes("user already registered") ||
    m.includes("already been registered")
  ) {
    return i18n.t("errors:account_already_exists");
  }
  if (m.includes("invalid login credentials")) {
    return i18n.t("errors:invalid_credentials");
  }
  if (m.includes("email rate limit")) {
    return i18n.t("errors:email_rate_limit");
  }
  if (m.includes("password should be")) {
    return i18n.t("errors:password_criteria");
  }
  if (m.includes("unable to validate email")) {
    return i18n.t("errors:invalid_email");
  }
  return message;
}

interface SignUpFormProps {
  onSuccess?: () => void;
  onSwitchToSignIn?: () => void;
  onFormChange?: (hasData: boolean) => void;
  /** Hide the card title/subtitle when the page already renders its own heading. */
  hideHeader?: boolean;
}

export function SignUpForm({
  onSuccess,
  onSwitchToSignIn,
  onFormChange,
  hideHeader = false,
}: SignUpFormProps) {
  const { t } = useTranslation();
  const { signUp, signInWithGoogle, loading, error } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGoogleSubmitting, setIsGoogleSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    setError,
    watch,
    control,
  } = useForm<SignUpInput>({
    resolver: zodResolver(SignUpSchema),
    mode: "onChange",
    defaultValues: {
      termsAccepted: false as unknown as true,
    },
  });

  // 🔧 FIX BUG #3: Détecter si des données sont saisies
  React.useEffect(() => {
    const subscription = watch((value) => {
      const hasData = !!(
        value.email ||
        value.password ||
        value.confirmPassword ||
        value.fullName
      );
      onFormChange?.(hasData);
    });
    return () => subscription.unsubscribe();
  }, [watch, onFormChange]);

  const handleGoogleSignIn = async () => {
    setIsGoogleSubmitting(true);

    try {
      const result = await signInWithGoogle();
      const error = result.error;

      if (error) {
        setError("root", {
          type: "manual",
          message: error,
        });
      } else {
        onSuccess?.();
      }
    } catch (err) {
      logError(
        ErrorFactory.validation(
          "Erreur lors de la connexion Google",
          "Une erreur inattendue s'est produite lors de la connexion Google",
        ),
        { metadata: { originalError: err } },
      );
      setError("root", {
        type: "manual",
        message: t("errors:google_unexpected"),
      });
    } finally {
      setIsGoogleSubmitting(false);
    }
  };

  const onSubmit = async (data: SignUpInput) => {
    setIsSubmitting(true);

    try {
      // Injecte les métadonnées de consentement RGPD dans raw_user_meta_data.
      // Le trigger SQL `handle_new_user` les lit pour peupler `profiles` et
      // créer la première ligne dans `user_consents` (preuve juridique art. 7).
      // termsAccepted est déjà validé par Zod (z.literal(true) dans SignUpSchema),
      // on peut donc utiliser `new Date()` en toute sécurité ici.
      // Attribution boucle virale (cookieless) : récupère ?cta=A|B|C de l'URL,
      // valide la valeur, et la transmet en métadonnée du compte.
      const ctaParam = new URLSearchParams(window.location.search).get("cta");
      const ctaVariant =
        ctaParam && ["A", "B", "C"].includes(ctaParam) ? ctaParam : undefined;

      const result = await signUp(data.email, data.password, {
        fullName: data.fullName,
        termsVersion: CURRENT_TERMS_VERSION,
        termsAcceptedAt: new Date().toISOString(),
        ctaVariant,
      });
      const error = result.error;

      if (error) {
        setError("root", {
          type: "manual",
          message: error,
        });
      } else {
        onSuccess?.();
      }
    } catch (err) {
      setError("root", {
        type: "manual",
        message: t("errors:generic"),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      {!hideHeader && (
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl text-center">
            {t("auth:sign_up")}
          </CardTitle>
          <CardDescription className="text-center">
            {t("auth:create_account_subtitle", { brand: getBrandName() })}
          </CardDescription>
        </CardHeader>
      )}

      <CardContent className="space-y-4">
        {/* Google Sign-In Button */}
        <Button
          type="button"
          variant="outline"
          className="w-full relative"
          disabled={isGoogleSubmitting || loading}
          onClick={handleGoogleSignIn}
          data-testid="signupform-button"
        >
          {isGoogleSubmitting && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          )}
          <LazyIcon
            name="Chrome"
            fallback={<div className="w-4 h-4 mr-2 bg-blue-500 rounded" />}
            className="mr-2 h-4 w-4"
          />
          {t("auth:continue_with_google")}
        </Button>

        {/* Divider */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">
              {t("auth:or_create_account_with_email")}
            </span>
          </div>
        </div>

        {/* Formulaire inscription */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="fullName">{t("auth:full_name")}</Label>
            <div className="relative">
              <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="fullName"
                type="text"
                autoComplete="name"
                maxLength={100}
                placeholder={t("auth:full_name_placeholder")}
                className="pl-10"
                data-testid="signupform-full-name"
                aria-invalid={!!errors.fullName || undefined}
                aria-describedby={
                  errors.fullName ? "fullName-error" : undefined
                }
                {...register("fullName")}
              />
            </div>
            {errors.fullName && (
              <p
                id="fullName-error"
                className="text-sm text-destructive"
                role="alert"
              >
                {errors.fullName.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">{t("auth:email")}</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="email"
                type="email"
                autoComplete="email"
                maxLength={100}
                placeholder={t("auth:email_placeholder")}
                className="pl-10"
                aria-invalid={!!errors.email || undefined}
                aria-describedby={errors.email ? "email-error" : undefined}
                {...register("email")}
              />
            </div>
            {errors.email && (
              <p
                id="email-error"
                className="text-sm text-destructive"
                role="alert"
              >
                {errors.email.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">{t("auth:password")}</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                maxLength={128}
                placeholder="••••••••"
                className="pl-10 pr-10"
                aria-invalid={!!errors.password || undefined}
                aria-describedby={
                  errors.password
                    ? "password-error password-hints"
                    : "password-hints"
                }
                {...register("password")}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
                aria-label={
                  showPassword
                    ? t("auth:hide_password")
                    : t("auth:show_password")
                }
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
            {/* 🔧 FIX BUG #4: Afficher toutes les contraintes dès le départ */}
            <div
              id="password-hints"
              className="text-xs text-muted-foreground space-y-1"
            >
              <p className="font-medium">{t("auth:password_must_contain")}</p>
              <ul className="list-disc list-inside space-y-0.5 ml-2">
                <li>{t("auth:password_rule_min_length")}</li>
                <li>{t("auth:password_rule_lowercase")}</li>
                <li>{t("auth:password_rule_uppercase")}</li>
                <li>{t("auth:password_rule_digit")}</li>
              </ul>
            </div>
            {errors.password && (
              <p
                id="password-error"
                className="text-sm text-destructive"
                role="alert"
              >
                {errors.password.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">
              {t("auth:confirm_password")}
            </Label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                autoComplete="new-password"
                maxLength={128}
                placeholder="••••••••"
                className="pl-10 pr-10"
                aria-invalid={!!errors.confirmPassword || undefined}
                aria-describedby={
                  errors.confirmPassword ? "confirmPassword-error" : undefined
                }
                {...register("confirmPassword")}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((v) => !v)}
                className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
                aria-label={
                  showConfirmPassword
                    ? t("auth:hide_password")
                    : t("auth:show_password")
                }
                tabIndex={-1}
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
            {errors.confirmPassword && (
              <p
                id="confirmPassword-error"
                className="text-sm text-destructive"
                role="alert"
              >
                {errors.confirmPassword.message}
              </p>
            )}
          </div>

          {/* Consentement RGPD (CGU + politique de confidentialité) */}
          <div className="space-y-2">
            <div className="flex items-start gap-2">
              <Controller
                name="termsAccepted"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    id="termsAccepted"
                    checked={!!field.value}
                    onCheckedChange={(checked) =>
                      field.onChange(checked === true)
                    }
                    className="mt-1"
                    data-testid="signupform-terms-accepted"
                  />
                )}
              />
              <Label
                htmlFor="termsAccepted"
                className="text-sm font-normal leading-snug cursor-pointer"
              >
                {t("auth:terms_consent_prefix")}{" "}
                <a
                  href="/docs/cg-u"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline text-primary"
                >
                  {t("legal:terms_of_service")}
                </a>{" "}
                {t("auth:terms_consent_and")}{" "}
                <a
                  href="/docs/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline text-primary"
                >
                  {t("legal:privacy_policy")}
                </a>
                {t("auth:terms_consent_suffix")}
              </Label>
            </div>
            {errors.termsAccepted && (
              <p className="text-sm text-destructive">
                {errors.termsAccepted.message as string}
              </p>
            )}
          </div>

          {/* Erreurs générales */}
          {(errors.root || error) && (
            <Alert
              variant="destructive"
              className="bg-destructive/15 text-destructive-foreground border-destructive"
            >
              <AlertDescription className="text-white">
                {translateAuthError(errors.root?.message || error || "")}
              </AlertDescription>
            </Alert>
          )}

          <Button
            type="submit"
            className="w-full"
            disabled={isSubmitting || loading || !isValid}
            data-testid="signupform-submit"
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t("auth:create_my_account")}
          </Button>
        </form>

        {/* Lien vers connexion */}
        <div className="text-center text-sm">
          <span className="text-muted-foreground">
            {t("auth:already_have_account")}{" "}
          </span>
          <Button
            type="button"
            variant="link"
            className="p-0 h-auto font-normal"
            onClick={onSwitchToSignIn}
            data-testid="signupform-se-connecter"
          >
            {t("auth:sign_in")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
